/**
 * Debugging utilities for i18n
 * Run these in the browser console to debug language detection
 */

import { detectClientLanguage } from "~/utils/languageDetection";
import { translations } from "./translations";
import { supportedLocales } from "./types";

declare global {
  interface Window {
    debugI18n?: () => void;
    resetI18n?: () => void;
  }
}

function debugI18n() {
  console.log("=== i18n Debug Info ===");
  console.log("navigator.language:", navigator.language);
  console.log("navigator.languages:", navigator.languages);
  console.log("localStorage['storytell-language']:", localStorage.getItem("storytell-language"));
  console.log("Cookie PARAGLIDE_LOCALE:", document.cookie.match(/PARAGLIDE_LOCALE=([^;]+)/)?.[1]);

  const detected = detectClientLanguage();
  console.log("detectClientLanguage() would return:", detected);

  console.log("Supported locales:", supportedLocales);

  const browserLang = navigator.language?.toLowerCase().split("-")[0] || "";
  console.log(
    "Current browser language is supported:",
    supportedLocales.some((loc) => loc.toLowerCase().startsWith(browserLang))
  );
  console.log("Browser language code breakdown:");
  console.log("  - Full code:", navigator.language);
  console.log("  - Language part:", browserLang);
  console.log("  - Full code (lowercase):", navigator.language?.toLowerCase());

  // Test Chinese variant matching
  if (navigator.language?.toLowerCase().startsWith("zh")) {
    console.log("\n=== Chinese Language Detection Test ===");
    const testCodes = [
      navigator.language,
      ...(navigator.languages || []).filter((l) => l.toLowerCase().startsWith("zh")),
    ];
    for (const code of testCodes) {
      console.log(`Testing code: "${code}"`);
      const lowerCode = code.toLowerCase();
      console.log(`  - Lowercase: "${lowerCode}"`);
      console.log(`  - Includes "hans": ${lowerCode.includes("hans")}`);
      console.log(`  - Includes "hant": ${lowerCode.includes("hant")}`);
      console.log(`  - Includes "cn": ${lowerCode.includes("cn")}`);
      console.log(`  - Includes "tw": ${lowerCode.includes("tw")}`);
      console.log(`  - Includes "hk": ${lowerCode.includes("hk")}`);
    }
  }

  // Check translation availability for all locales
  console.log("\n=== Translation Availability ===");
  for (const locale of supportedLocales) {
    const dict = translations[locale];
    const sampleValue = dict ? Object.values(dict)[0] : null;
    console.log(`- ${locale}:`, {
      dictExists: !!dict,
      keyCount: dict ? Object.keys(dict).length : 0,
      sampleValue: sampleValue?.substring(0, 50),
    });
  }
}

function resetI18n() {
  console.log("=== Resetting i18n Preferences ===");
  localStorage.removeItem("storytell-language");
  // Clear all possible cookie variations
  document.cookie = "PARAGLIDE_LOCALE=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  document.cookie = "storytell-language=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  // Also clear from all possible domains
  const hostname = window.location.hostname;
  document.cookie = `PARAGLIDE_LOCALE=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname};`;
  document.cookie = `PARAGLIDE_LOCALE=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${hostname};`;

  console.log("✅ Cleared localStorage and cookies");
  console.log("🔄 Reload the page to test browser language detection");
  console.log("📝 Note: Server may still detect from Accept-Language header, but client will override");
}

// Make available globally for console debugging
if (typeof window !== "undefined") {
  window.debugI18n = debugI18n;
  window.resetI18n = resetI18n;
}
